import * as THREE from 'three';
import { bus } from '@/core/EventBus';
import type { CombatTarget } from '@/gameplay/Combat';
import type { Enemy } from '@/gameplay/Enemy';
import { Vehicle } from '@/gameplay/Vehicle';
import { advanceVehicle } from '@/gameplay/vehicleMotion';
import type { CollisionWorld } from '@/world/collision';

export type ChaseLevel = 'none' | 'searching' | 'active' | 'escaping';

export interface ChaseConfig {
  readonly detectRadius?: number;
  readonly escapeSeconds?: number;
  readonly escapeDistance?: number;
  readonly reinforcements?: number;
}

export interface ChaseSnapshot {
  readonly level: ChaseLevel;
  readonly escapeProgress: number;
  readonly visibleThreats: number;
  readonly nearbyThreats: number;
  readonly lastKnownPosition: THREE.Vector2 | null;
}

export interface VehicleChaseConfig {
  readonly id: string;
  readonly vehicle: Vehicle;
  readonly speedLimit?: number;
  readonly stopDistance?: number;
}

interface VehicleChaser {
  readonly id: string;
  readonly vehicle: Vehicle;
  readonly speedLimit: number;
  readonly stopDistance: number;
}

export const CHASE = {
  detectRadius: 16,
  escapeSeconds: 4,
  escapeDistance: 13,
  vehicleSpeedLimit: 6.2,
  vehicleAcceleration: 4.2,
  vehicleTurnRate: 2.2,
  vehicleStopDistance: 3.2,
} as const;

export class ChaseSystem {
  readonly #target: CombatTarget;
  readonly #world: CollisionWorld;
  readonly #config: Required<ChaseConfig>;
  readonly #enemies = new Set<Enemy>();
  readonly #vehicleChasers: VehicleChaser[] = [];
  readonly #lastKnown = new THREE.Vector2();
  #hasLastKnown = false;
  #level: ChaseLevel = 'none';
  #escapeElapsed = 0;
  #started = false;
  #unsubscribeAlert: (() => void) | null = null;
  #unsubscribeShot: (() => void) | null = null;

  constructor(target: CombatTarget, world: CollisionWorld, config: ChaseConfig = {}) {
    this.#target = target;
    this.#world = world;
    this.#config = {
      detectRadius: config.detectRadius ?? CHASE.detectRadius,
      escapeSeconds: config.escapeSeconds ?? CHASE.escapeSeconds,
      escapeDistance: config.escapeDistance ?? CHASE.escapeDistance,
      reinforcements: config.reinforcements ?? 0,
    };

    this.#unsubscribeAlert = bus.on('enemy:alerted', () => this.start('alert'));
    this.#unsubscribeShot = bus.on('combat:shot', ({ faction }) => {
      if (faction === 'vitale') this.start('shot');
    });
  }

  get level(): ChaseLevel {
    return this.#level;
  }

  get escapeProgress(): number {
    return THREE.MathUtils.clamp(this.#escapeElapsed / this.#config.escapeSeconds, 0, 1);
  }

  registerEnemy(enemy: Enemy): void {
    this.#enemies.add(enemy);
  }

  removeEnemy(enemy: Enemy): void {
    this.#enemies.delete(enemy);
  }

  registerVehicleChaser(config: VehicleChaseConfig): void {
    config.vehicle.state = 'driving';
    this.#vehicleChasers.push({
      id: config.id,
      vehicle: config.vehicle,
      speedLimit: config.speedLimit ?? CHASE.vehicleSpeedLimit,
      stopDistance: config.stopDistance ?? CHASE.vehicleStopDistance,
    });
  }

  start(reason: 'alert' | 'shot'): void {
    if (this.#level === 'none') {
      this.#started = true;
      this.#level = 'active';
      this.#rememberTarget();
      bus.emit('chase:started', { level: this.#level, reason });
      return;
    }
    this.#started = true;
    this.#level = 'active';
    this.#escapeElapsed = 0;
    this.#rememberTarget();
  }

  update(dt: number, allVehicles: readonly Vehicle[] = []): void {
    if (!this.#started || !this.#target.alive) return;

    const visibleThreats = this.#visibleThreats();
    const nearbyThreats = this.#nearbyThreats();
    if (visibleThreats > 0 || nearbyThreats > 0) {
      this.#level = 'active';
      this.#escapeElapsed = 0;
      this.#rememberTarget();
    } else if (this.#level === 'active') {
      this.#level = 'searching';
      this.#escapeElapsed = 0;
    } else {
      this.#level = 'escaping';
      this.#escapeElapsed += dt;
      if (this.#escapeElapsed >= this.#config.escapeSeconds) {
        const seconds = this.#escapeElapsed;
        this.#reset();
        bus.emit('chase:escaped', { seconds });
        return;
      }
    }

    for (const chaser of this.#vehicleChasers) this.#updateVehicleChaser(chaser, dt, allVehicles);
  }

  fail(reason: string): void {
    if (this.#level === 'none') return;
    this.#reset();
    bus.emit('chase:failed', { reason });
  }

  snapshot(): ChaseSnapshot {
    return {
      level: this.#level,
      escapeProgress: this.escapeProgress,
      visibleThreats: this.#visibleThreats(),
      nearbyThreats: this.#nearbyThreats(),
      lastKnownPosition: this.#hasLastKnown ? this.#lastKnown.clone() : null,
    };
  }

  dispose(): void {
    this.#unsubscribeAlert?.();
    this.#unsubscribeShot?.();
    this.#unsubscribeAlert = null;
    this.#unsubscribeShot = null;
    this.#enemies.clear();
    this.#vehicleChasers.length = 0;
  }

  #visibleThreats(): number {
    let count = 0;
    for (const enemy of this.#enemies) {
      if (!enemy.alive) continue;
      if (enemy.state === 'chase' || enemy.state === 'shoot') count++;
    }
    return count;
  }

  #nearbyThreats(): number {
    let count = 0;
    for (const enemy of this.#enemies) {
      if (!enemy.alive) continue;
      if (enemy.position.distanceTo(this.#target.position) <= this.#config.detectRadius) count++;
    }
    for (const chaser of this.#vehicleChasers) {
      if (chaser.vehicle.state === 'burned') continue;
      if (chaser.vehicle.position.distanceTo(this.#target.position) <= this.#config.detectRadius) count++;
    }
    return count;
  }

  #rememberTarget(): void {
    this.#lastKnown.copy(this.#target.position);
    this.#hasLastKnown = true;
  }

  #reset(): void {
    this.#level = 'none';
    this.#escapeElapsed = 0;
    this.#started = false;
    this.#hasLastKnown = false;
    for (const chaser of this.#vehicleChasers) chaser.vehicle.speed = 0;
  }

  #updateVehicleChaser(chaser: VehicleChaser, dt: number, allVehicles: readonly Vehicle[]): void {
    if (!this.#hasLastKnown || this.#level === 'none' || chaser.vehicle.occupied) return;
    const vehicle = chaser.vehicle;
    if (vehicle.state === 'burned') return;

    const distance = vehicle.position.distanceTo(this.#lastKnown);
    if (distance <= chaser.stopDistance) {
      vehicle.speed = moveToward(vehicle.speed, 0, CHASE.vehicleAcceleration * dt);
      return;
    }

    const desiredRotation = Math.atan2(this.#lastKnown.x - vehicle.position.x, this.#lastKnown.y - vehicle.position.y);
    vehicle.rotation = rotateToward(vehicle.rotation, desiredRotation, CHASE.vehicleTurnRate * dt);
    vehicle.speed = moveToward(vehicle.speed, chaser.speedLimit, CHASE.vehicleAcceleration * dt);

    const blockers = allVehicles.filter((candidate) => candidate !== vehicle && candidate.state !== 'burned');
    const advance = advanceVehicle(
      vehicle,
      vehicle.speed * dt,
      vehicle.collisionHalfWidth,
      vehicle.collisionHalfLength,
      this.#world,
      blockers,
    );

    // Een achtervolger die een hoek meepakt schuurt erlangs; alleen frontaal staat hij stil.
    if (advance.blocked && !advance.slid) vehicle.speed = 0;

    vehicle.object.position.set(vehicle.position.x, 0, vehicle.position.y);
    vehicle.object.rotation.y = vehicle.rotation;
  }
}

function moveToward(value: number, target: number, amount: number): number {
  if (value < target) return Math.min(target, value + amount);
  if (value > target) return Math.max(target, value - amount);
  return value;
}

function rotateToward(current: number, target: number, amount: number): number {
  const delta = Math.atan2(Math.sin(target - current), Math.cos(target - current));
  if (Math.abs(delta) <= amount) return target;
  return current + Math.sign(delta) * amount;
}
